import { useRef, useEffect, useState, type CSSProperties } from "react";
import { GROUPS, accessRows, type AccessRow, type Role, type World } from "../sendItEngine";
import { FILE_META, FILE_ORDER, ROLE_LABEL, fileOf, personOf, placeLabel, placeOf, type FileId, type MissionId } from "./content";
import { Avatar, FileGlyph, Ic, type IcName } from "./icons";
import { tx, txr } from "../../lib/tx";

const ROLE_ICON: Record<Role, IcName> = { view: "eye", comment: "comment", edit: "pencil" };

interface RowInfo {
  name: string;
  sub: string;
  via: string;
  note?: string;
}

function describe(row: AccessRow): RowInfo {
  const person = row.personId ? personOf(row.personId) : undefined;
  const group = row.groupId ? GROUPS.find((g) => g.id === row.groupId) : undefined;
  switch (row.kind) {
    case "direct":
      return { name: person?.name ?? "Someone", sub: person?.email ?? "", via: "Shared directly" };
    case "parent":
      return {
        name: person?.name ?? group?.name ?? "Someone",
        sub: person?.email ?? "",
        via: "From the folder \u201CWorkshop archive\u201D",
        note: "Comes with the folder, not with this file. Sharing this file again does not change it.",
      };
    case "group":
      return { name: group?.name ?? "A group", sub: `${group?.memberIds.length ?? 0} people`, via: "Shared with the group" };
    case "link":
      return { name: "Anyone with the link", sub: "No sign-in needed", via: "Link" };
    case "named-link":
      return {
        name: person?.name ?? "Someone",
        sub: person?.email ?? "",
        via: row.expired ? "Time-limited link \u00B7 ended" : "Time-limited link \u00B7 ends with the workshop",
      };
    case "guest":
      return {
        name: person?.name ?? "Someone",
        sub: person?.email ?? "",
        via: "Project guest",
        note: "A guest of the Workshop project. Can comment on project files.",
      };
  }
}

function RowAvatar({ row }: { row: AccessRow }) {
  if (row.kind === "link") return <span className="sis-ava-ic"><Ic n="globe" size={17} /></span>;
  if (row.kind === "group") {
    const g = GROUPS.find((x) => x.id === row.groupId);
    return (
      <span className="sis-stack" aria-hidden="true">
        {(g?.memberIds ?? []).map((id) => {
          const m = personOf(id);
          return m ? <Avatar key={id} name={m.name} org={m.org} size={26} /> : null;
        })}
      </span>
    );
  }
  const p = row.personId ? personOf(row.personId) : undefined;
  return <Avatar name={p?.name ?? "?"} org={p?.org} size={34} />;
}

/** The Manage access window: every separate route into a file, one row each. */
export function AccessWin({
  world,
  fileId,
  missionId,
  onFile,
  onRemove,
  onRequestLocation,
  onConfirm,
  onShare,
}: {
  world: World;
  fileId: FileId;
  missionId: MissionId;
  onFile: (id: FileId) => void;
  onRemove: (row: AccessRow) => void;
  onRequestLocation: () => void;
  onConfirm: () => void;
  onShare: (id: FileId) => void;
}) {
  const [pending, setPending] = useState<Record<string, "asking" | "leaving">>({});
  const [locating, setLocating] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const t = timers.current;
    return () => t.forEach((h) => window.clearTimeout(h));
  }, []);

  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const file = fileOf(fileId);
  const meta = FILE_META[fileId];
  const place = placeOf(world, fileId);
  const rows = accessRows(world, fileId);

  const drop = (key: string) =>
    setPending((p) => Object.fromEntries(Object.entries(p).filter(([k]) => k !== key)) as Record<string, "asking" | "leaving">);

  const remove = (row: AccessRow) => {
    const ask = row.kind === "guest";
    setPending((p) => ({ ...p, [row.key]: ask ? "asking" : "leaving" }));
    later(
      () => {
        setPending((p) => ({ ...p, [row.key]: "leaving" }));
        later(() => {
          onRemove(row);
          drop(row.key);
        }, 240);
      },
      ask ? 850 : 0,
    );
  };

  const askLocation = () => {
    setLocating(true);
    later(() => {
      onRequestLocation();
      setLocating(false);
    }, 950);
  };

  return (
    <div className="sis-ax">
      <div className="sis-ax-top">
        <div className="sis-ax-file">
          <FileGlyph ext={meta.ext} size={32} />
          <div>
            <strong>{file.name}</strong>
            <span>{tx("Everyone and everything that can open this file")}</span>
          </div>
        </div>
        <div className="sis-chips" role="tablist" aria-label={tx("Choose a file")}>
          {FILE_ORDER.map((id) => {
            const n = accessRows(world, id).filter((r) => !r.expired).length;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={id === fileId}
                className={`sis-chip${id === fileId ? " is-on" : ""}`}
                onClick={() => onFile(id)}
              >
                {FILE_META[id].short}
                {n > 0 && <em>{n}</em>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="sis-ax-body sis-scroll">
        <div className={`sis-loc${place === "shared-folder" ? " is-warn" : ""}`} key={`${fileId}-${place}`}>
          <span className="sis-loc-ic"><Ic n="folder" size={18} /></span>
          <div className="sis-loc-tx">
            <strong>{placeLabel(world, fileId)}</strong>
            <span>
              {place === "shared-folder"
                ? tx("This file takes its access from its folder. Anyone given the folder can still open it, whatever you share on this file.")
                : tx("Access is set on this file only.")}
            </span>
          </div>
          {place === "shared-folder" && (
            <button type="button" className="sis-btn" onClick={askLocation} disabled={locating}>
              {locating ? (<><span className="sis-spin" aria-hidden="true" /> {tx("Asking the owner…")}</>) : tx("Request a restricted project location")}
            </button>
          )}
        </div>

        <div className="sis-ax-h">{tx("People and links with access")}</div>
        <div className="sis-arows" role="list">
          <div className="sis-arow is-owner" role="listitem">
            <span className="sis-ava-ic is-you"><Ic n="user" size={17} /></span>
            <div className="sis-arow-tx">
              <strong>{tx("You")}</strong>
              <span>{tx("Owner")}</span>
            </div>
            <span className="sis-pill sis-pill--own"><Ic n="shield" size={12} /> {tx("Owner")}</span>
          </div>
          {rows.map((row, i) => {
            const info = describe(row);
            const st = pending[row.key];
            return (
              <div
                key={row.key}
                role="listitem"
                className={`sis-arow is-${row.kind}${row.expired ? " is-expired" : ""}${st === "leaving" ? " is-leaving" : ""}`}
                style={{ "--i": i } as CSSProperties}
              >
                <RowAvatar row={row} />
                <div className="sis-arow-tx">
                  <strong>{info.name}</strong>
                  {info.sub && <span>{info.sub}</span>}
                  <em>{info.via}</em>
                  {info.note && <span className="sis-arow-note">{info.note}</span>}
                </div>
                <span className={`sis-pill sis-pill--${row.role}`}>
                  <Ic n={ROLE_ICON[row.role]} size={12} /> {txr("Can {v}", { v: ROLE_LABEL[row.role].toLowerCase() })}
                </span>
                {row.kind === "parent" ? (
                  <span className="sis-inherit" title={tx("Inherited from the folder")}>
                    <Ic n="lock" size={12} /> {tx("Folder access")}
                  </span>
                ) : (
                  <button type="button" className="sis-btn sis-btn--sm sis-btn--danger" onClick={() => remove(row)} disabled={!!st}>
                    {st === "asking" ? (<><span className="sis-spin" aria-hidden="true" /> {tx("Asking the owner…")}</>) : (
                      <><Ic n="trash" size={12} /> {row.kind === "guest" ? tx("Ask owner to remove") : tx("Remove")}</>
                    )}
                  </button>
                )}
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="sis-none">{tx("Nobody else can open this file yet. Use Share to give someone access.")}</div>
          )}
        </div>

        {missionId === "m6" && fileId === "external" && (
          <div className="sis-copy" role="note">
            <span className="sis-copy-ic"><Ic n="download" size={18} /></span>
            <div>
              <span className="sis-tag">{tx("Simulated")}</span>
              <strong>{tx("Downloaded copy on Alex Kim's computer")}</strong>
              <p>{tx("Alex saved this guide earlier. Removing access here cannot recall or delete that copy.")}</p>
            </div>
          </div>
        )}
      </div>

      <footer className="sis-ax-foot">
        <button type="button" className="sis-btn" onClick={() => onShare(fileId)}>
          <Ic n="share" size={14} /> {tx("Share this file")}
        </button>
        {missionId === "m6" && (
          <button type="button" className="sis-btn sis-btn--primary" onClick={onConfirm}>
            <Ic n="check" size={14} /> {tx("Confirm access is closed")}
          </button>
        )}
      </footer>
    </div>
  );
}
